/**
 * CommunityPostCard — one item on the community feed (2026-09-10 rebuild).
 *
 * A card says who, when and what, and offers three one-tap reactions. There
 * is no category rail, no diagnosis chip, no age group, no space — the
 * research behind the rebuild found no peer organisation labelling posts by
 * diagnosis, and a card that opens with "Brain tumor" is not something a
 * parent should meet on a support page.
 *
 * Two kinds share the shape: TRF's own announcements (author line "From
 * TRF") and family notes (author line is the family's chosen name or
 * "A TRF family"). Reactions are optimistic via the page's react/unreact
 * mutations; this component only wires the visual layer.
 *
 * w1023c: the one pinned announcement carries a "Pinned" mark beside its
 * author line, and `preview` draws the card exactly as the feed does for the
 * staff "Preview" (composer and row), with the reactions shown but inert.
 *
 * #2426: a tap says what it did ("Heart added") in a polite, visually hidden
 * live region, because the count and aria-pressed alone are silent. A
 * reaction whose request is still in flight is aria-disabled, never
 * `disabled`, so the button the family just pressed keeps keyboard focus.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HandHeart, Heart, PartyPopper, Pin, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COARSE_HIT_BEHIND } from '@/lib/touch-target';
import { MONO_LABEL_TYPE, PendingDecision, StatusChip } from '@/components/editorial';
import { timeAgo } from '@/lib/utils';
import {
  ANNOUNCEMENT_AUTHOR_KEY,
  ANONYMOUS_AUTHOR_KEY,
  PINNED_KEY,
  REACTIONS,
  STATUS_TOKENS,
  type ReactionKey,
} from '@/lib/community-display';
import { resolveMsg, msg } from '@/i18n/msg';
import type { CommunityPost } from '@/types/api';

interface CommunityPostCardProps {
  /** `pinned` arrives on announcements since w1023c; older rows omit it. */
  post: CommunityPost & { pinned?: boolean };
  userId: string;
  onReact: (postId: string, type: ReactionKey, currentList: string[]) => void;
  /** This post's reactions whose request is in flight. Only those ignore a tap. */
  pendingReactions?: readonly ReactionKey[];
  /** When true, render the author-only status chip (waiting / live / not published). */
  showStatus?: boolean;
  /** The page passes this on ONE card so the reactions decision marker appears once, on the thing it is about. */
  showReactionDecision?: boolean;
  /** Staff "Preview as family": the same card, reactions drawn but inert. */
  preview?: boolean;
}

const BODY_PREVIEW_LENGTH = 280;

const NO_PENDING: readonly ReactionKey[] = [];

/** Drawn icons, not emoji (#1951): they take the button's colour, so the
 *  pressed state tints them indigo like every other control on the page. */
const REACTION_ICONS: Record<ReactionKey, LucideIcon> = {
  heart: Heart,
  hug: HandHeart,
  celebrate: PartyPopper,
};

export function CommunityPostCard({
  post,
  userId,
  onReact,
  pendingReactions = NO_PENDING,
  showStatus = false,
  showReactionDecision = false,
  preview = false,
}: CommunityPostCardProps) {
  const { t } = useTranslation('community');
  const [expanded, setExpanded] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const isLong = post.body.length > BODY_PREVIEW_LENGTH;
  const bodyText = isLong && !expanded ? post.body.slice(0, BODY_PREVIEW_LENGTH).trimEnd() + '…' : post.body;
  const isAnnouncement = post.kind === 'announcement';
  const status = showStatus ? STATUS_TOKENS[post.status] : null;
  // The anonymous line is ours, not the family's: the server stores English
  // "A TRF family", so the card renders the key instead (R3 L6).
  const authorLine = isAnnouncement
    ? resolveMsg(t, msg(ANNOUNCEMENT_AUTHOR_KEY))
    : post.anonymous
      ? resolveMsg(t, msg(ANONYMOUS_AUTHOR_KEY))
      : post.author_display;
  // Reactions are only offered on a live post — a pending note has no audience yet.
  const canReact = post.status === 'visible';
  const pinned = isAnnouncement && post.pinned === true;

  return (
    <article className="rounded-lg border border-border bg-card">
      <header className="flex items-start gap-3 px-5 pt-4 pb-1 flex-wrap">
        <p className={cn(MONO_LABEL_TYPE, 'flex-1 min-w-0 flex items-center gap-2 text-stevens-ink-3')}>
          <span
            className={cn('truncate', isAnnouncement ? 'text-stevens-indigo' : 'text-stevens-ink')}
            translate={isAnnouncement || post.anonymous ? undefined : 'no'}
          >
            {authorLine}
          </span>
          <span aria-hidden>·</span>
          <time dateTime={post.created_at} className="font-mono font-normal whitespace-nowrap">
            {timeAgo(post.created_at)}
          </time>
          {pinned && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1 text-stevens-indigo" data-testid="post-pinned">
                <Pin aria-hidden className="h-3 w-3" />
                {resolveMsg(t, msg(PINNED_KEY))}
              </span>
            </>
          )}
        </p>
        {status && <StatusChip tone={status.tone}>{resolveMsg(t, msg(status.labelKey))}</StatusChip>}
      </header>

      {post.title && (
        <h3 className="px-5 pb-1 font-serif text-card-title leading-tight text-foreground">
          {post.title}
        </h3>
      )}

      <p className="px-5 pb-3 text-body text-foreground/90 whitespace-pre-wrap leading-relaxed">
        {bodyText}
      </p>
      {isLong && (
        <button
          type="button"
          className="ml-5 mb-3 text-caption text-stevens-indigo hover:text-stevens-indigo-2 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40 focus-visible:rounded-sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('card.showLess') : t('card.readMore')}
        </button>
      )}

      {/* #2603 (D-2420): the three reactions draw 36x32 and reach 44x44 on a
          coarse pointer. `isolate` makes the footer the stacking context, so
          each -z-10 hit area sits under every drawn button; the coarse gap is
          8 px, twice the 4 px side reach, so no two hit areas overlap. */}
      {canReact && (
        <footer className="isolate flex items-center gap-1 [@media(pointer:coarse)]:gap-2 px-3 py-2 border-t border-border/50">
          {REACTIONS.map((r) => {
            const list = post.reactions?.[r.key] ?? [];
            const count = list.length;
            const active = list.includes(userId);
            const Icon = REACTION_ICONS[r.key];
            const label = resolveMsg(t, msg(r.labelKey));
            const pending = preview || pendingReactions.includes(r.key);
            return (
              <button
                key={r.key}
                type="button"
                aria-label={count > 0 ? t('reaction.withCount', { label, n: count }) : label}
                aria-pressed={active}
                title={label}
                aria-disabled={pending || undefined}
                onClick={() => {
                  if (pending) return;
                  setAnnouncement(t(active ? 'reaction.removed' : 'reaction.added', { label }));
                  onReact(post._id, r.key, list);
                }}
                className={cn(
                  'inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-body transition-colors',
                  COARSE_HIT_BEHIND,
                  '[@media(pointer:coarse)]:after:-inset-x-1 [@media(pointer:coarse)]:after:-inset-y-1.5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40',
                  'aria-disabled:cursor-default aria-disabled:opacity-50',
                  active
                    ? 'bg-stevens-indigo/10 text-stevens-indigo'
                    : 'text-stevens-ink-3 hover:bg-muted hover:text-foreground',
                )}
              >
                <Icon aria-hidden className="h-4 w-4" />
                {count > 0 && (
                  <span aria-hidden className="font-mono text-caption tabular-nums">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          {showReactionDecision && !preview && <PendingDecision id={['community-reactions', 'reaction-confirmation']} className="ml-1" />}
          <span className="sr-only" aria-live="polite" aria-atomic="true">
            {announcement}
          </span>
        </footer>
      )}
    </article>
  );
}
